import {getAgentSessionsTokenProvider} from '@github-ui/copilot-auth-token/agent-sessions-token'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {makeCAPIRequest} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {getEnv} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopilotAuthTokenProvider} from '@github-ui/copilot-auth-token'

const DEFAULT_API_URL = 'https://api.githubcopilot.com/'

interface FetchWithAuthOptions {
  path: string
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  streamingResponse?: boolean
  basePath?: string
  body?: object
  retryOn401?: boolean
}

export class FetchCAPIError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'FetchCAPIError'
    this.status = status
  }
}

export async function fetchCAPI({
  path,
  method = 'GET',
  streamingResponse = false,
  body,
  retryOn401 = false,
}: FetchWithAuthOptions): Promise<Response> {
  const useCAPITokenProvider = isFeatureEnabled('mission_control_use_capi_token_provider')
  const tokenProvider = useCAPITokenProvider ? new CopilotAuthTokenProvider([]) : getAgentSessionsTokenProvider()
  const basePath = getEnv().copilotApiOverrideUrl ?? DEFAULT_API_URL

  return requestWithAuthRetry({path, method, streamingResponse, body, basePath, retryOn401}, tokenProvider)
}

interface RequestWithRetryOptions {
  path: string
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  streamingResponse: boolean
  basePath: string
  body?: object
  retryOn401: boolean
}

const MAX_AUTH_RETRIES = 1

async function requestWithAuthRetry(
  requestOptions: RequestWithRetryOptions,
  tokenProvider: CopilotAuthTokenProvider,
  retryAttempt = 0,
): Promise<Response> {
  const token = await tokenProvider.getAuthToken()

  if (!token) {
    throw new Error('No token available')
  }

  const response = await makeCAPIRequest({
    authToken: token,
    basePath: requestOptions.basePath,
    method: requestOptions.method,
    path: requestOptions.path,
    streamingResponse: requestOptions.streamingResponse,
    body: requestOptions.body,
  })

  if (!response.ok) {
    if (requestOptions.retryOn401 && response.status === 401 && retryAttempt < MAX_AUTH_RETRIES) {
      const safeTokenClearing = isFeatureEnabled('mission_control_safe_token_clearing')
      const currentToken = tokenProvider.getLocalStorageAuthToken()
      // Add check to only clear token if it matches the one we used in this request
      // This prevents a race condition where multiple requests could clear a newly fetched token
      if (!safeTokenClearing || currentToken?.value === token.value) {
        tokenProvider.removeLocalStorageAuthToken()
      }
      return requestWithAuthRetry(requestOptions, tokenProvider, retryAttempt + 1)
    }

    let errorMessage = 'error' in response ? response.error : `Failed CAPI request (${response.status})`

    // Try to get the actual error message from the response body (it's plain text)
    if ('response' in response && response.response) {
      try {
        const text = await response.response.text()
        if (text && text.trim()) {
          errorMessage = text.trim()
        }
      } catch {
        // If we can't read the response body, keep the generic message
      }
    }

    throw new FetchCAPIError(errorMessage, response.status)
  }

  return response
}
